import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface Document {
  id: string;
  order_id: string;
  file_url: string;
  file_name: string;
  file_type: string;
  file_size: number;
  uploaded_by: string;
  created_at: string;
  uploader?: {
    first_name: string | null;
    last_name: string | null;
    email: string;
  };
}

export const useDocuments = (orderId: string) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ['documents', orderId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('documents')
        .select(`
          *,
          uploader:uploaded_by (
            first_name,
            last_name,
            email
          )
        `)
        .eq('order_id', orderId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Document[];
    },
    enabled: !!orderId,
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!user) throw new Error('User not authenticated');

      // Upload to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${orderId}/${Date.now()}.${fileExt}`;
      
      const { error: uploadError, data: uploadData } = await supabase.storage
        .from('delivery_documents')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('delivery_documents')
        .getPublicUrl(fileName);

      // Insert document record
      const { data: document, error: dbError } = await supabase
        .from('documents')
        .insert({
          order_id: orderId,
          file_url: publicUrl,
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
          uploaded_by: user.id,
        })
        .select()
        .single();

      if (dbError) throw dbError;
      return document;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', orderId] });
      toast.success('Document uploadé avec succès');
    },
    onError: (error: Error) => {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload du document');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (documentId: string) => {
      // Get document to delete from storage
      const document = documents.find(d => d.id === documentId);
      if (!document) throw new Error('Document not found');

      // Delete from database first
      const { error: dbError } = await supabase
        .from('documents')
        .delete()
        .eq('id', documentId);

      if (dbError) throw dbError;

      // Extract storage path from URL
      const urlParts = document.file_url.split('/delivery_documents/');
      if (urlParts.length > 1) {
        const storagePath = urlParts[1];
        const { error: storageError } = await supabase.storage
          .from('delivery_documents')
          .remove([storagePath]);

        if (storageError) console.error('Storage delete error:', storageError);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', orderId] });
      toast.success('Document supprimé');
    },
    onError: (error: Error) => {
      console.error('Delete error:', error);
      toast.error('Erreur lors de la suppression');
    },
  });

  return {
    documents,
    isLoading,
    uploadDocument: uploadMutation.mutate,
    isUploading: uploadMutation.isPending,
    deleteDocument: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
};
